import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3,
  Package,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Download,
  FileText,
  Filter,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard } from '@/components/ui/glass-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth';
import { apiFetch } from '@/lib/api';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

type ReportTab = 'vendas' | 'estoque' | 'credito' | 'comparativo';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('pt-BR');
}

function formatShortDate(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length >= 3) return `${parts[2]}/${parts[1]}`;
  return dateStr;
}

function getDefaultStartDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().split('T')[0]!;
}

function getDefaultEndDate(): string {
  return new Date().toISOString().split('T')[0]!;
}

function formatMonthLabel(monthStr: string): string {
  const months: Record<string, string> = {
    '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
    '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
    '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
  };
  const parts = monthStr.split('-');
  if (parts.length === 2) return `${months[parts[1]!] || parts[1]}/${parts[0]!.slice(2)}`;
  return monthStr;
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

// ─── PDF GENERATION ──────────────────────────────────────────────────────────

interface PDFOptions {
  title: string;
  period?: string;
  summaryItems: { label: string; value: string }[];
  headers: string[];
  rows: string[][];
  totals?: string[];
}

function generatePDF(options: PDFOptions, tenantName: string) {
  const { title, period, summaryItems, headers, rows, totals } = options;
  const logo = localStorage.getItem('FluxoPDV_logo');
  const now = new Date();
  const generatedAt = now.toLocaleString('pt-BR');

  const logoHtml = logo
    ? `<img src="${logo}" alt="Logo" style="max-height:60px;max-width:180px;object-fit:contain;" />`
    : '';

  const summaryHtml = summaryItems
    .map((item) => `<tr><td style="padding:6px 12px;font-weight:bold;border:1px solid #ddd;">${item.label}</td><td style="padding:6px 12px;border:1px solid #ddd;">${item.value}</td></tr>`)
    .join('');

  const headerHtml = headers.map((h) => `<th style="padding:8px 10px;border:1px solid #999;background:#f0f0f0;font-size:11px;text-align:left;">${h}</th>`).join('');

  const rowsHtml = rows
    .map((row, i) => {
      const bg = i % 2 === 1 ? 'background:#f9f9f9;' : '';
      const cells = row.map((cell) => `<td style="padding:6px 10px;border:1px solid #ddd;font-size:11px;${bg}">${cell}</td>`).join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  const totalsHtml = totals
    ? `<tr style="font-weight:bold;background:#e8e8e8;">${totals.map((t) => `<td style="padding:8px 10px;border:1px solid #999;font-size:11px;">${t}</td>`).join('')}</tr>`
    : '';

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <title>${title} - ${tenantName}</title>
  <style>
    @page { size: A4; margin: 15mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #333; padding: 20px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #333; }
    .header-left { display: flex; align-items: center; gap: 15px; }
    .header-right { text-align: right; }
    .header-right h1 { font-size: 18px; margin-bottom: 4px; }
    .header-right p { font-size: 11px; color: #666; }
    .summary-table { margin-bottom: 20px; border-collapse: collapse; }
    .data-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
    .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #ccc; display: flex; justify-content: space-between; font-size: 10px; color: #666; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      ${logoHtml}
      <div>
        <strong style="font-size:14px;">${tenantName}</strong>
      </div>
    </div>
    <div class="header-right">
      <h1>${title}</h1>
      ${period ? `<p>Período: ${period}</p>` : ''}
      <p>Gerado em: ${generatedAt}</p>
    </div>
  </div>

  <table class="summary-table">
    ${summaryHtml}
  </table>

  <table class="data-table">
    <thead><tr>${headerHtml}</tr></thead>
    <tbody>${rowsHtml}${totalsHtml}</tbody>
  </table>

  <div class="footer">
    <span>Gerado por FluxoPDV</span>
    <span>Nilsom Neto - Análise e Desenvolvimento de Sistemas</span>
  </div>

  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
}

// ─── CSV EXPORT ──────────────────────────────────────────────────────────────

function exportCSV(filename: string, headers: string[], rows: string[][]) {
  const BOM = '\uFEFF';
  const csvHeaders = headers.join(';');
  const csvRows = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(';'));
  const csvContent = BOM + [csvHeaders, ...csvRows].join('\r\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}


// ─── TYPES ───────────────────────────────────────────────────────────────────

interface SalesReport {
  summary: { total_sales: number; total_value: number; average_ticket: number };
  payment_breakdown: { method: string; total: number; count: number }[];
  top_products: { product_name: string; quantity: number; revenue: number }[];
  daily_sales: { date: string; count: number; total: number }[];
}

interface InventoryReport {
  summary: {
    total_products: number;
    total_stock_value_cost: number;
    total_stock_value_sale: number;
    low_stock_count: number;
    out_of_stock_count: number;
  };
  all_products: {
    id: string; name: string; barcode: string; category: string;
    stock_quantity: number; min_stock_level: number;
    cost_price: number; sale_price: number; status: string; unit: string;
  }[];
  low_stock_items: {
    id: string; name: string; category: string;
    stock_quantity: number; min_stock_level: number;
    cost_price: number; sale_price: number;
  }[];
  out_of_stock_items: { id: string; name: string; category: string }[];
}

interface CreditReport {
  summary: {
    total_receivable: number; total_overdue: number;
    total_on_time: number; total_debtors: number; total_debts: number;
  };
  debts: {
    id: string; customer_id: string; customer_name: string;
    customer_cpf: string; customer_phone: string;
    amount: number; purchase_date: string; due_date: string;
    status: string; payment_date: string | null;
  }[];
  top_debtors: { id: string; name: string; total_debt: number }[];
}

interface MonthlyReport {
  months: {
    month: string; total_sales: number; sale_count: number;
    average_ticket: number; growth_pct: number | null;
  }[];
  period_total: number;
  period_count: number;
}

const PAYMENT_LABELS: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  cartao_debito: 'Débito',
  cartao_credito: 'Crédito',
  credito: 'Crédito (Fiado)',
  fiado: 'Crédito (Fiado)',
};

// ─── VENDAS TAB ──────────────────────────────────────────────────────────────

function VendasTab() {
  const { tenant } = useAuth();
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [method, setMethod] = useState('');
  const [operator, setOperator] = useState('');
  const [category, setCategory] = useState('');
  const [operators, setOperators] = useState<{id: string; name: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SalesReport | null>(null);
  const debouncedCategory = useDebounce(category, 300);

  useEffect(() => {
    apiFetch('/api/users?limit=100').then(res => res.ok ? res.json() : null).then(d => {
      if (d?.data) setOperators(d.data.map((u: {id: string; name: string}) => ({ id: u.id, name: u.name })));
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (method) params.set('method', method);
      if (operator) params.set('operator', operator);
      if (debouncedCategory) params.set('category', debouncedCategory);

      const res = await apiFetch(`/api/reports/sales?${params.toString()}`);
      if (!res.ok) throw new Error('Erro ao carregar relatório de vendas');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, method, operator, debouncedCategory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ['Produto', 'Quantidade', 'Receita'];
    const rows = data.top_products.map((p) => [
      p.product_name,
      String(p.quantity),
      formatCurrency(p.revenue),
    ]);
    exportCSV('relatorio-vendas', headers, rows);
    toast.success('CSV exportado com sucesso');
  };

  const handleExportPDF = () => {
    if (!data) return;
    generatePDF(
      {
        title: 'Relatório de Vendas',
        period: `${formatDate(startDate)} a ${formatDate(endDate)}`,
        summaryItems: [
          { label: 'Total de Vendas', value: String(data.summary.total_sales) },
          { label: 'Valor Total', value: formatCurrency(data.summary.total_value) },
          { label: 'Ticket Médio', value: formatCurrency(data.summary.average_ticket) },
        ],
        headers: ['Produto', 'Quantidade', 'Receita'],
        rows: data.top_products.map((p) => [
          p.product_name,
          String(p.quantity),
          formatCurrency(p.revenue),
        ]),
        totals: ['TOTAL', '', formatCurrency(data.summary.total_value)],
      },
      tenant?.name || 'FluxoPDV',
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <GlassCard className="p-8 text-center">
        <p className="text-base text-muted-foreground">Nenhum dado disponível</p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">De:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Até:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
            />
          </div>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          >
            <option value="">Forma Pagamento</option>
            <option value="dinheiro">Dinheiro</option>
            <option value="pix">PIX</option>
            <option value="cartao_debito">Cartão Débito</option>
            <option value="cartao_credito">Cartão Crédito</option>
            <option value="fiado">Crédito</option>
          </select>
          <select
            value={operator}
            onChange={(e) => setOperator(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          >
            <option value="">Operador</option>
            {operators.map((op) => (
              <option key={op.id} value={op.id}>{op.name}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Categoria"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground w-36"
          />
          <div className="ml-auto flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 rounded-lg bg-green-600/20 px-3 py-2 text-sm text-green-400 hover:bg-green-600/30 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1 rounded-lg bg-red-600/20 px-3 py-2 text-sm text-red-400 hover:bg-red-600/30 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              PDF
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total Vendas</p>
          <p className="text-2xl font-bold text-foreground">{data.summary.total_sales}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Valor Total</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.total_value)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Ticket Médio</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.average_ticket)}</p>
        </GlassCard>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground">Vendas Diárias</h3>
          {data.daily_sales.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.daily_sales.map((d) => ({ ...d, label: formatShortDate(d.date) }))}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
                <XAxis dataKey="label" stroke="#A0A0B0" fontSize={12} />
                <YAxis stroke="#A0A0B0" fontSize={12} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1A1A24', border: '1px solid #2D2D3D', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFFFFF' }}
                  formatter={(value: number) => [formatCurrency(value), 'Total']}
                />
                <Area type="monotone" dataKey="total" stroke="#10B981" fill="url(#colorSales)" name="Total" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-base text-muted-foreground text-center py-10">Sem dados no período</p>
          )}
        </GlassCard>

        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground">Por Forma de Pagamento</h3>
          {data.payment_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.payment_breakdown.map((p) => ({ ...p, label: PAYMENT_LABELS[p.method] || p.method }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
                <XAxis dataKey="label" stroke="#A0A0B0" fontSize={11} />
                <YAxis stroke="#A0A0B0" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1A1A24', border: '1px solid #2D2D3D', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFFFFF' }}
                  formatter={(value: number) => [formatCurrency(value), 'Total']}
                />
                <Bar dataKey="total" fill="#00D2D3" radius={[4, 4, 0, 0]} name="Total" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-base text-muted-foreground text-center py-10">Sem dados</p>
          )}
        </GlassCard>
      </div>

      {/* Produtos mais vendidos */}
      <GlassCard>
        <h3 className="mb-4 text-base font-semibold text-foreground">Produtos Mais Vendidos</h3>
        <DataTable
          columns={[
            { key: 'product_name', header: 'Produto', sortable: true },
            { key: 'quantity', header: 'Quantidade', sortable: true },
            {
              key: 'revenue',
              header: 'Receita',
              sortable: true,
              render: (row) => <span className="font-medium">{formatCurrency(row.revenue)}</span>,
            },
          ]}
          data={data.top_products}
          getRowKey={(_, i) => String(i)}
          emptyMessage="Nenhum produto encontrado"
        />
      </GlassCard>
    </div>
  );
}


// ─── ESTOQUE TAB ─────────────────────────────────────────────────────────────

function EstoqueReportTab() {
  const { tenant } = useAuth();
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [supplier, setSupplier] = useState('');
  const [suppliers, setSuppliers] = useState<{id: string; company_name: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<InventoryReport | null>(null);
  const debouncedCategory = useDebounce(category, 300);

  useEffect(() => {
    apiFetch('/api/suppliers?limit=100').then(res => res.ok ? res.json() : null).then(d => {
      if (d?.data) setSuppliers(d.data);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedCategory) params.set('category', debouncedCategory);
      if (status) params.set('status', status);
      if (supplier) params.set('supplier', supplier);

      const res = await apiFetch(`/api/reports/inventory?${params.toString()}`);
      if (!res.ok) throw new Error('Erro ao carregar relatório de estoque');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [debouncedCategory, status, supplier]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ['Produto', 'Categoria', 'Estoque Atual', 'Nível Mínimo', 'Preço Custo', 'Preço Venda', 'Status'];
    const rows = data.all_products.map((p) => [
      p.name,
      p.category,
      String(p.stock_quantity),
      String(p.min_stock_level),
      formatCurrency(p.cost_price),
      formatCurrency(p.sale_price),
      p.status,
    ]);
    exportCSV('relatorio-estoque', headers, rows);
    toast.success('CSV exportado com sucesso');
  };

  const handleExportPDF = () => {
    if (!data) return;
    generatePDF(
      {
        title: 'Relatório de Estoque',
        summaryItems: [
          { label: 'Total Produtos', value: String(data.summary.total_products) },
          { label: 'Valor Total (Custo)', value: formatCurrency(data.summary.total_stock_value_cost) },
          { label: 'Valor Total (Venda)', value: formatCurrency(data.summary.total_stock_value_sale) },
          { label: 'Estoque Baixo', value: `${data.summary.low_stock_count} itens` },
          { label: 'Esgotados', value: `${data.summary.out_of_stock_count} itens` },
        ],
        headers: ['Produto', 'Categoria', 'Estoque', 'Nível Mín.', 'Preço Custo', 'Preço Venda', 'Status'],
        rows: data.all_products.map((p) => [
          p.name,
          p.category,
          String(p.stock_quantity),
          String(p.min_stock_level),
          formatCurrency(p.cost_price),
          formatCurrency(p.sale_price),
          p.status === 'normal' ? 'Normal' : p.status === 'baixo' ? 'Baixo' : 'Esgotado',
        ]),
      },
      tenant?.name || 'FluxoPDV',
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <GlassCard className="p-8 text-center">
        <p className="text-base text-muted-foreground">Nenhum dado disponível</p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Categoria"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          >
            <option value="">Status</option>
            <option value="normal">Normal</option>
            <option value="baixo">Baixo</option>
            <option value="esgotado">Esgotado</option>
          </select>
          <select
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          >
            <option value="">Fornecedor</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.company_name}</option>
            ))}
          </select>
          <div className="ml-auto flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 rounded-lg bg-green-600/20 px-3 py-2 text-sm text-green-400 hover:bg-green-600/30 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1 rounded-lg bg-red-600/20 px-3 py-2 text-sm text-red-400 hover:bg-red-600/30 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              PDF
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total Produtos</p>
          <p className="text-2xl font-bold text-foreground">{data.summary.total_products}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Valor Total (Custo)</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.total_stock_value_cost)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Estoque Baixo</p>
          <p className="text-2xl font-bold text-yellow-400">{data.summary.low_stock_count} itens</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Esgotados</p>
          <p className="text-2xl font-bold text-red-400">{data.summary.out_of_stock_count} itens</p>
        </GlassCard>
      </div>

      {/* Tabela de estoque baixo */}
      {data.low_stock_items.length > 0 && (
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground flex items-center gap-2">
            <Package className="h-4 w-4 text-yellow-400" />
            Produtos com Estoque Baixo ({data.low_stock_items.length})
          </h3>
          <DataTable
            columns={[
              { key: 'name', header: 'Produto', sortable: true },
              { key: 'category', header: 'Categoria' },
              {
                key: 'stock_quantity',
                header: 'Estoque Atual',
                render: (row) => (
                  <span className="font-medium text-yellow-400">{row.stock_quantity}</span>
                ),
              },
              { key: 'min_stock_level', header: 'Nível Mínimo' },
              {
                key: 'cost_price',
                header: 'Preço Custo',
                render: (row) => formatCurrency(row.cost_price),
              },
              {
                key: 'sale_price',
                header: 'Preço Venda',
                render: (row) => formatCurrency(row.sale_price),
              },
            ]}
            data={data.low_stock_items}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhum produto com estoque baixo"
          />
        </GlassCard>
      )}

      {/* Tabela completa */}
      <GlassCard>
        <h3 className="mb-4 text-base font-semibold text-foreground">
          Todos os Produtos ({data.all_products.length})
        </h3>
        <DataTable
          columns={[
            { key: 'name', header: 'Produto', sortable: true },
            { key: 'category', header: 'Categoria' },
            {
              key: 'stock_quantity',
              header: 'Estoque',
              sortable: true,
              render: (row) => (
                <span className={`font-medium ${row.status === 'esgotado' ? 'text-red-400' : row.status === 'baixo' ? 'text-yellow-400' : ''}`}>
                  {row.stock_quantity}
                </span>
              ),
            },
            { key: 'min_stock_level', header: 'Mínimo' },
            { key: 'cost_price', header: 'Custo', render: (row) => formatCurrency(row.cost_price) },
            { key: 'sale_price', header: 'Venda', render: (row) => formatCurrency(row.sale_price) },
            {
              key: 'status',
              header: 'Status',
              render: (row) => (
                <Badge variant={row.status === 'normal' ? 'success' : row.status === 'baixo' ? 'warning' : 'destructive'}>
                  {row.status === 'normal' ? 'Normal' : row.status === 'baixo' ? 'Baixo' : 'Esgotado'}
                </Badge>
              ),
            },
          ]}
          data={data.all_products}
          getRowKey={(row) => row.id}
          emptyMessage="Nenhum produto encontrado"
        />
      </GlassCard>
    </div>
  );
}


// ─── CRÉDITO TAB ─────────────────────────────────────────────────────────────

function CreditoTab() {
  const { tenant } = useAuth();
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CreditReport | null>(null);
  const debouncedCustomer = useDebounce(customerFilter, 300);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (debouncedCustomer) params.set('customer', debouncedCustomer);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);

      const res = await apiFetch(`/api/reports/credit?${params.toString()}`);
      if (!res.ok) throw new Error('Erro ao carregar relatório de crédito');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedCustomer, startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ['Cliente', 'CPF', 'Telefone', 'Valor', 'Data Compra', 'Vencimento', 'Status'];
    const rows = data.debts.map((d) => [
      d.customer_name,
      d.customer_cpf || '',
      d.customer_phone || '',
      formatCurrency(d.amount),
      formatDate(d.purchase_date),
      formatDate(d.due_date),
      d.status === 'vencido' ? 'Vencido' : d.status === 'em_dia' ? 'Em dia' : 'Pago',
    ]);
    exportCSV('relatorio-credito', headers, rows);
    toast.success('CSV exportado com sucesso');
  };

  const handleExportPDF = () => {
    if (!data) return;
    generatePDF(
      {
        title: 'Relatório de Crédito',
        summaryItems: [
          { label: 'Total a Receber', value: formatCurrency(data.summary.total_receivable) },
          { label: 'Total Vencido', value: formatCurrency(data.summary.total_overdue) },
          { label: 'Total Em Dia', value: formatCurrency(data.summary.total_on_time) },
          { label: 'Devedores', value: String(data.summary.total_debtors) },
        ],
        headers: ['Cliente', 'Valor', 'Data Compra', 'Vencimento', 'Status'],
        rows: data.debts.map((d) => [
          d.customer_name,
          formatCurrency(d.amount),
          formatDate(d.purchase_date),
          formatDate(d.due_date),
          d.status === 'vencido' ? 'Vencido' : d.status === 'em_dia' ? 'Em dia' : 'Pago',
        ]),
        totals: ['TOTAL', formatCurrency(data.summary.total_receivable), '', '', ''],
      },
      tenant?.name || 'FluxoPDV',
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <GlassCard className="p-8 text-center">
        <p className="text-base text-muted-foreground">Nenhum dado disponível</p>
      </GlassCard>
    );
  }

  const debtColumns: Column<CreditReport['debts'][0]>[] = [
    { key: 'customer_name', header: 'Cliente', sortable: true },
    {
      key: 'amount',
      header: 'Valor',
      sortable: true,
      render: (row) => <span className="font-medium">{formatCurrency(row.amount)}</span>,
    },
    {
      key: 'purchase_date',
      header: 'Data Compra',
      render: (row) => <span>{formatDate(row.purchase_date)}</span>,
    },
    {
      key: 'due_date',
      header: 'Vencimento',
      sortable: true,
      render: (row) => formatDate(row.due_date),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <Badge variant={row.status === 'vencido' ? 'destructive' : row.status === 'pago' ? 'success' : 'warning'}>
          {row.status === 'vencido' ? 'Vencido' : row.status === 'pago' ? 'Pago' : 'Em dia'}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          >
            <option value="">Todos os Status</option>
            <option value="em_dia">Em dia</option>
            <option value="vencido">Vencido</option>
            <option value="pago">Pago</option>
          </select>
          <input
            type="text"
            placeholder="Cliente (nome)"
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground w-40"
          />
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">De:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-muted-foreground">Até:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
            />
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 rounded-lg bg-green-600/20 px-3 py-2 text-sm text-green-400 hover:bg-green-600/30 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1 rounded-lg bg-red-600/20 px-3 py-2 text-sm text-red-400 hover:bg-red-600/30 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              PDF
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total a Receber</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(data.summary.total_receivable)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total Vencido</p>
          <p className="text-2xl font-bold text-red-400">{formatCurrency(data.summary.total_overdue)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Devedores</p>
          <p className="text-2xl font-bold text-foreground">{data.summary.total_debtors}</p>
        </GlassCard>
      </div>

      {/* Tabela de débitos */}
      <GlassCard>
        <h3 className="mb-4 text-base font-semibold text-foreground flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-red-400" />
          Débitos ({data.debts.length})
        </h3>
        <DataTable
          columns={debtColumns}
          data={data.debts}
          getRowKey={(row) => row.id}
          emptyMessage="Nenhum débito encontrado"
        />
      </GlassCard>

      {/* Maiores devedores */}
      {data.top_debtors.length > 0 && (
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground">Maiores Devedores</h3>
          <DataTable
            columns={[
              { key: 'name', header: 'Cliente', sortable: true },
              {
                key: 'total_debt',
                header: 'Total Dívida',
                sortable: true,
                render: (row) => (
                  <span className="font-medium text-red-400">{formatCurrency(row.total_debt)}</span>
                ),
              },
            ]}
            data={data.top_debtors}
            getRowKey={(row) => row.id}
          />
        </GlassCard>
      )}
    </div>
  );
}


// ─── COMPARATIVO TAB ─────────────────────────────────────────────────────────

function ComparativoTab() {
  const { tenant } = useAuth();
  const [months, setMonths] = useState('6');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<MonthlyReport | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/reports/monthly-comparison?months=${months}`);
      if (!res.ok) throw new Error('Erro ao carregar comparativo mensal');
      const json = await res.json();
      setData(json);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = () => {
    if (!data) return;
    const headers = ['Mês', 'Total Vendas', 'Qtd Vendas', 'Ticket Médio', 'Variação (%)'];
    const rows = data.months.map((m) => [
      formatMonthLabel(m.month),
      formatCurrency(m.total_sales),
      String(m.sale_count),
      formatCurrency(m.average_ticket),
      m.growth_pct !== null ? `${m.growth_pct.toFixed(1)}%` : '—',
    ]);
    exportCSV('comparativo-mensal', headers, rows);
    toast.success('CSV exportado com sucesso');
  };

  const handleExportPDF = () => {
    if (!data) return;
    generatePDF(
      {
        title: 'Comparativo Mensal de Vendas',
        period: `Últimos ${months} meses`,
        summaryItems: [
          { label: 'Total no Período', value: formatCurrency(data.period_total) },
          { label: 'Vendas no Período', value: String(data.period_count) },
          { label: 'Ticket Médio Geral', value: formatCurrency(data.period_count > 0 ? data.period_total / data.period_count : 0) },
        ],
        headers: ['Mês', 'Total Vendas', 'Qtd Vendas', 'Ticket Médio', 'Variação'],
        rows: data.months.map((m) => [
          formatMonthLabel(m.month),
          formatCurrency(m.total_sales),
          String(m.sale_count),
          formatCurrency(m.average_ticket),
          m.growth_pct !== null ? `${m.growth_pct >= 0 ? '+' : ''}${m.growth_pct.toFixed(1)}%` : '—',
        ]),
        totals: ['TOTAL', formatCurrency(data.period_total), String(data.period_count), '', ''],
      },
      tenant?.name || 'FluxoPDV',
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <GlassCard className="p-8 text-center">
        <p className="text-base text-muted-foreground">Nenhum dado disponível</p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* Seletor de período */}
      <GlassCard className="p-4">
        <div className="flex items-center gap-4">
          <label className="text-base text-muted-foreground">Período:</label>
          <select
            value={months}
            onChange={(e) => setMonths(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-base text-foreground"
          >
            <option value="3">Últimos 3 meses</option>
            <option value="6">Últimos 6 meses</option>
            <option value="12">Últimos 12 meses</option>
          </select>
          <div className="ml-auto flex gap-2">
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1 rounded-lg bg-green-600/20 px-3 py-2 text-sm text-green-400 hover:bg-green-600/30 transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
            <button
              onClick={handleExportPDF}
              className="flex items-center gap-1 rounded-lg bg-red-600/20 px-3 py-2 text-sm text-red-400 hover:bg-red-600/30 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              PDF
            </button>
          </div>
        </div>
      </GlassCard>

      {/* Resumo do período */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total no Período</p>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(data.period_total)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Vendas no Período</p>
          <p className="text-2xl font-bold text-foreground">{data.period_count}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Ticket Médio Geral</p>
          <p className="text-2xl font-bold text-foreground">
            {formatCurrency(data.period_count > 0 ? data.period_total / data.period_count : 0)}
          </p>
        </GlassCard>
      </div>

      {/* Gráfico de tendência */}
      <GlassCard>
        <h3 className="mb-4 text-base font-semibold text-foreground">Tendência de Vendas</h3>
        {data.months.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data.months.map((m) => ({ ...m, label: formatMonthLabel(m.month) }))}>
              <defs>
                <linearGradient id="colorLine" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#00D2D3" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
              <XAxis dataKey="label" stroke="#A0A0B0" fontSize={12} />
              <YAxis stroke="#A0A0B0" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1A1A24', border: '1px solid #2D2D3D', borderRadius: '8px' }}
                labelStyle={{ color: '#FFFFFF' }}
                formatter={(value: number) => [formatCurrency(value), 'Total']}
              />
              <Line
                type="monotone"
                dataKey="total_sales"
                stroke="url(#colorLine)"
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 5 }}
                name="Total Vendas"
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-base text-muted-foreground text-center py-10">Sem dados no período</p>
        )}
      </GlassCard>

      {/* Tabela comparativa */}
      <GlassCard>
        <h3 className="mb-4 text-base font-semibold text-foreground">Comparativo Mensal</h3>
        <DataTable
          columns={[
            {
              key: 'month',
              header: 'Mês',
              sortable: true,
              render: (row) => <span className="font-medium">{formatMonthLabel(row.month)}</span>,
            },
            {
              key: 'total_sales',
              header: 'Total Vendas',
              sortable: true,
              render: (row) => <span className="font-medium">{formatCurrency(row.total_sales)}</span>,
            },
            { key: 'sale_count', header: 'Qtd Vendas', sortable: true },
            {
              key: 'average_ticket',
              header: 'Ticket Médio',
              render: (row) => formatCurrency(row.average_ticket),
            },
            {
              key: 'growth_pct',
              header: 'Variação',
              render: (row) => {
                if (row.growth_pct === null) return <span className="text-muted-foreground">—</span>;
                const isPositive = row.growth_pct >= 0;
                const isDecline = row.growth_pct < -10;
                return (
                  <span className={`flex items-center gap-1 font-medium ${isDecline ? 'text-red-400' : isPositive ? 'text-green-400' : 'text-yellow-400'}`}>
                    {isPositive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {isPositive ? '+' : ''}{row.growth_pct.toFixed(1)}%
                  </span>
                );
              },
            },
          ]}
          data={data.months}
          getRowKey={(_, i) => String(i)}
          emptyMessage="Nenhum dado disponível"
        />
      </GlassCard>
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

const tabs: { id: ReportTab; label: string; icon: React.ReactNode }[] = [
  { id: 'vendas', label: 'Vendas', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'estoque', label: 'Estoque', icon: <Package className="h-4 w-4" /> },
  { id: 'credito', label: 'Crédito', icon: <CreditCard className="h-4 w-4" /> },
  { id: 'comparativo', label: 'Comparativo', icon: <TrendingUp className="h-4 w-4" /> },
];

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>('vendas');

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Relatórios</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-card p-1 border border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-md px-4 py-2.5 text-base font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conteúdo da aba */}
      {activeTab === 'vendas' && <VendasTab />}
      {activeTab === 'estoque' && <EstoqueReportTab />}
      {activeTab === 'credito' && <CreditoTab />}
      {activeTab === 'comparativo' && <ComparativoTab />}
    </div>
  );
}
